import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, 
  AlertCircle, 
  RefreshCw, 
  Gavel,
  Calendar,
  DollarSign,
  Package,
  TrendingUp,
  Eye
} from 'lucide-react';
import { fetchClienteLances } from '../services/api';
import { useToast } from '../hooks/use-toast';

const MeusLances = ({ user }) => {
  const [lances, setLances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Check if user is logged in and has clienteId
    if (!user?.isLoggedIn || !user?.clienteId) {
      navigate('/login');
      return;
    }
    
    loadLances();
  }, [user, navigate]);

  const loadLances = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Ensure we have a valid clienteId
      if (!user?.clienteId) {
        setError('ID do cliente não encontrado. Faça login novamente.');
        return;
      }
      
      console.log('Carregando lances para cliente ID:', user.clienteId);
      const lancesData = await fetchClienteLances(user.clienteId);
      console.log('Lances recebidos:', lancesData);
      
      setLances(lancesData || []);
      
    } catch (err) {
      console.error('Erro ao carregar lances:', err);
      setError(err.message || 'Erro ao carregar seus lances');
      
      toast({
        title: "Erro ao carregar lances",
        description: err.message || 'Não foi possível carregar seus lances.',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Agrupar lances por lote para mostrar histórico
  const groupedLances = lances.reduce((acc, lance) => {
    const key = `${lance.IDLOTE}-${lance.LOTEXX}`;
    if (!acc[key]) {
      acc[key] = {
        ...lance,
        lances: []
      };
    }
    acc[key].lances.push(lance);
    return acc;
  }, {});

  // Ordenar lances por valor (maior para menor) dentro de cada grupo
  Object.values(groupedLances).forEach(group => {
    group.lances.sort((a, b) => b.VALOR - a.VALOR);
    group.maiorLance = group.lances[0].VALOR;
    group.totalLances = group.lances.length;
  });

  const sortedGroups = Object.values(groupedLances).sort((a, b) => b.maiorLance - a.maiorLance);

  const navigateToLeilao = (idLeilao) => {
    navigate(`/leilao/${idLeilao}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gray-50 text-gray-900 py-16 border-b-4 border-[rgb(25,42,86)]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </div>
            
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-black mb-2 text-[rgb(25,42,86)]">
                Meus Lances
              </h1>
              <p className="text-xl opacity-90">
                Carregando seu histórico de lances...
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <RefreshCw className="h-6 w-6 animate-spin text-[rgb(46,48,146)]" />
              <span className="text-lg">Carregando lances...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gray-50 text-gray-900 py-16 border-b-4 border-[rgb(25,42,86)]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </div>
            
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-black mb-2 text-[rgb(25,42,86)]">
                Meus Lances
              </h1>
              <p className="text-xl opacity-90">
                Erro ao carregar lances
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Erro ao carregar lances</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            
            <Button
              onClick={loadLances}
              className="bg-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/90"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-50 text-gray-900 py-16 border-b-4 border-[rgb(25,42,86)]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Gavel className="h-12 w-12 mr-4 text-[rgb(25,42,86)]" />
              <h1 className="text-3xl md:text-4xl font-black text-[rgb(25,42,86)]">
                Meus Lances
              </h1>
            </div>
            <p className="text-xl opacity-90">
              Histórico completo dos seus lances
            </p>
            
            {/* Estatísticas */}
            <div className="flex items-center justify-center mt-8 space-x-8">
              <div className="text-center">
                <div className="text-2xl font-bold">{sortedGroups.length}</div>
                <div className="text-sm opacity-75">Lotes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{lances.length}</div>
                <div className="text-sm opacity-75">Lances</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {formatCurrency(lances.reduce((sum, lance) => sum + lance.VALOR, 0))}
                </div>
                <div className="text-sm opacity-75">Total</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Lances */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {sortedGroups.length === 0 ? (
          <div className="text-center py-16">
            <Gavel className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Nenhum lance encontrado</h3>
            <p className="text-gray-600 mb-6">
              Você ainda não fez nenhum lance. Explore nossos leilões ativos!
            </p>
            
            <Button
              onClick={() => navigate('/')}
              className="bg-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/90"
            >
              <Package className="h-4 w-4 mr-2" />
              Ver Leilões
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedGroups.map((group) => (
              <Card key={`${group.IDLOTE}-${group.LOTEXX}`} className="border-2 hover:border-[rgb(46,48,146)] transition-all duration-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Badge variant="secondary" className="text-lg font-bold">
                        Lote {group.LOTEXX}
                      </Badge>
                      <Badge className="bg-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/90">
                        {group.totalLances} lance{group.totalLances !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateToLeilao(group.IDLEILAO)}
                      className="hover:bg-[rgb(46,48,146)] hover:text-white"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Ver Leilão
                    </Button>
                  </div>
                  
                  <CardTitle className="text-lg leading-tight">
                    {group.DESLOT}
                  </CardTitle>
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-1" />
                    {group.LEILAO}
                  </div>
                </CardHeader>

                <CardContent>
                  {/* Maior Lance (Destaque) */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="h-5 w-5 text-green-600" />
                        <span className="font-medium text-green-800">Seu maior lance</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(group.maiorLance)}
                      </div>
                    </div>
                  </div>

                  {/* Histórico de Lances */}
                  {group.lances.length > 1 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-700 mb-2">Histórico de lances:</h4>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {group.lances.map((lance, index) => (
                          <div 
                            key={`${lance.IDLOTE}-${lance.VALOR}-${index}`}
                            className={`flex items-center justify-between p-2 rounded text-sm ${
                              index === 0 
                                ? 'bg-green-100 text-green-800 font-medium' 
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            <span>Lance #{group.lances.length - index}</span>
                            <span className="font-medium">
                              {formatCurrency(lance.VALOR)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Informações do Leilão */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Leiloeiro:</span>
                        <div className="font-medium">{group.NOMEXX}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">ID do Lote:</span>
                        <div className="font-medium">#{group.IDLOTE}</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Resumo Final */}
            <Card className="bg-gradient-to-r from-[rgb(46,48,146)]/5 to-[rgb(238,29,37)]/5 border-2">
              <CardContent className="p-6">
                <div className="text-center">
                  <h3 className="text-lg font-semibold mb-4">Resumo dos Seus Lances</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg">
                      <DollarSign className="h-8 w-8 text-[rgb(46,48,146)] mx-auto mb-2" />
                      <div className="text-2xl font-bold text-[rgb(46,48,146)]">
                        {formatCurrency(lances.reduce((sum, lance) => sum + lance.VALOR, 0))}
                      </div>
                      <div className="text-sm text-gray-600">Valor Total Investido</div>
                    </div>
                    
                    <div className="text-center p-4 bg-white rounded-lg">
                      <Gavel className="h-8 w-8 text-[rgb(238,29,37)] mx-auto mb-2" />
                      <div className="text-2xl font-bold text-[rgb(238,29,37)]">
                        {lances.length}
                      </div>
                      <div className="text-sm text-gray-600">Total de Lances</div>
                    </div>
                    
                    <div className="text-center p-4 bg-white rounded-lg">
                      <Package className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-green-600">
                        {sortedGroups.length}
                      </div>
                      <div className="text-sm text-gray-600">Lotes Participados</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeusLances;